<script setup lang="ts">
import type { AxiosError } from 'axios';
import { ref, computed } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { salariesApi, type Salary } from '@/api/salaries';
import { formatCurrency } from '@/utils/currency';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import SalaryForm from '@/components/caregivers/salary-form.vue';
import SectionHeader from '@/components/section-header.vue';

const props = withDefaults(defineProps<{
  caregiverId: number;
  inline?: boolean;
}>(), {
  inline: false,
});

const emit = defineEmits<{
  close: [];
}>();

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

const showForm = ref(false);
const editingSalary = ref<Salary | null>(null);
const deletingSalary = ref<Salary | null>(null);
const showDeleteConfirm = ref(false);

const FIVE_MINUTES_IN_MS = 300_000;

const { data: salaries, isLoading, isError } = useQuery({
  queryKey: ['salaries', props.caregiverId],
  queryFn: () => salariesApi.index(props.caregiverId),
  staleTime: FIVE_MINUTES_IN_MS,
});

const {
  mutate: deleteSalary,
  isPending: isDeletePending,
} = useMutation<void, AxiosError, number>({
  mutationFn: (id: number) => salariesApi.destroy(id),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['salaries', props.caregiverId] });
    queryClient.invalidateQueries({ queryKey: ['caregiver', props.caregiverId] });
    showDeleteConfirm.value = false;
    deletingSalary.value = null;
  },
});

const headers = [
  { title: 'Desde', value: 'humanStartsOn', sortable: true },
  { title: 'Hasta', value: 'humanEndsOn', sortable: true },
  { title: 'Sueldo Base Líquido', value: 'baseSalary', sortable: true },
  { title: 'Variable por Turno', value: 'amountPerShift', sortable: true },
  { title: 'Umbral', value: 'minimumShiftsThreshold', sortable: true },
  { title: 'Estado', value: 'active', sortable: true },
  { title: 'Acciones', value: 'actions', sortable: false },
];

const activeSalary = computed(() => salaries.value?.find((s) => s.active));

function openCreateForm() {
  editingSalary.value = null;
  showForm.value = true;
}

function openEditForm(salary: Salary) {
  editingSalary.value = salary;
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
  editingSalary.value = null;
}

function onFormSaved() {
  closeForm();
}

function confirmDelete(salary: Salary) {
  deletingSalary.value = salary;
  showDeleteConfirm.value = true;
}

function cancelDelete() {
  deletingSalary.value = null;
  showDeleteConfirm.value = false;
}

function executeDelete() {
  if (deletingSalary.value) {
    deleteSalary(deletingSalary.value.id);
  }
}
</script>

<template>
  <v-card>
    <v-card-text class="pa-5">
      <section-header
        icon="mdi-account-cash"
        title="Historial de Sueldos"
        class="mb-5"
      >
        <v-btn
          v-if="hasPermission('service_prices', 'create')"
          color="primary"
          variant="outlined"
          size="small"
          class="ml-3"
          @click="openCreateForm"
        >
          <v-icon
            icon="mdi-plus"
            class="mr-1"
          />
          Nuevo Sueldo
        </v-btn>
      </section-header>
      <v-alert
        v-if="activeSalary"
        type="success"
        variant="tonal"
        class="mb-4"
      >
        <strong>Sueldo Activo:</strong>
        Sueldo Base Líquido: {{ formatCurrency(activeSalary.baseSalary) }} |
        Variable: {{ formatCurrency(activeSalary.amountPerShift) }} por turno
        <template v-if="activeSalary.minimumShiftsThreshold > 0">
          (después de {{ activeSalary.minimumShiftsThreshold }} turnos)
        </template>
      </v-alert>

      <v-alert
        v-else-if="!isLoading && salaries?.length === 0"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Este profesional no tiene sueldos configurados.
      </v-alert>

      <v-data-table
        v-if="salaries && salaries.length > 0"
        v-bind="tableProps"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="salaries"
        :loading="isLoading"
        :sort-by="[{ key: 'startsOn', order: 'desc' }]"
      >
        <template #item.baseSalary="{ item }">
          {{ formatCurrency(item.baseSalary) }}
        </template>
        <template #item.amountPerShift="{ item }">
          {{ formatCurrency(item.amountPerShift) }}
        </template>
        <template #item.humanEndsOn="{ item }">
          {{ item.humanEndsOn || 'Activo' }}
        </template>
        <template #item.active="{ item }">
          <v-chip
            :color="item.active ? 'success' : 'grey'"
            size="small"
          >
            {{ item.active ? 'Activo' : 'Finalizado' }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-btn
            v-if="hasPermission('service_prices', 'update')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-btn
            v-if="hasPermission('service_prices', 'update')"
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
      </v-data-table>

      <v-alert
        v-if="isError"
        type="error"
        class="mt-4"
      >
        Error al cargar los sueldos.
      </v-alert>
    </v-card-text>

    <v-card-actions v-if="!props.inline">
      <v-btn
        variant="text"
        @click="emit('close')"
      >
        Cerrar
      </v-btn>
    </v-card-actions>

    <v-dialog
      v-model="showForm"
      max-width="600"
    >
      <salary-form
        :caregiver-id="props.caregiverId"
        :salary="editingSalary"
        @close="closeForm"
        @saved="onFormSaved"
      />
    </v-dialog>

    <v-dialog
      v-model="showDeleteConfirm"
      max-width="400"
    >
      <v-card>
        <v-card-title>Confirmar Eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este sueldo?
          <br>
          <strong>Desde:</strong> {{ deletingSalary?.humanStartsOn }}
          <br>
          <strong>Hasta:</strong> {{ deletingSalary?.humanEndsOn || 'Activo' }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            @click="cancelDelete"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            :loading="isDeletePending"
            @click="executeDelete"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

